import { ActionIcon, Avatar, Box, Center, Group, Menu, Modal, Popover, Skeleton, Text, Tooltip, UnstyledButton } from '@mantine/core';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import advancedFormats from 'dayjs/plugin/advancedFormat';
import LinkIcon from '../../components/icons/Link';
import AppRegistrationIcon from '../../components/icons/AppRegistration';
import LiveHelpIcon from '../../components/icons/LiveHelp';
import MoreVertIcon from '../../components/icons/MoreVert';
import OpenNewIcon from '../../components/icons/OpenNew';
import { DateFormats } from '../../utils/globals';
import { useMemo } from 'react';
import { generateSeatGeekUrl, generateTicketEvolutionUrl, generateTicketNetworkUrl } from '../../utils/urls';
import { BrokerNerdsBarkerCoreEnumsMarketplace, useGetApiMarketplacesMarketplaceIdEventsEventId } from '../../api';
import { useSeatingChart } from './SeatingChart.hooks';
import { formatDate } from '../../utils/formatters';
import SyncIcon from '../../components/icons/Sync';
import { useDisclosure } from '@mantine/hooks';
import { SupportForm } from '../Support/SupportForm';
import CheckBoxIcon from '../../components/icons/CheckBox';
import RocketLaunchIcon from '../../components/icons/RocketLaunch';
import CachedIcon from '../../components/icons/Cached';
import SyncAltIcon from '../../components/icons/SyncAlt';
import { useGlobalState } from '../../data/Global.state';
import classes from './SeatingChart.module.css';
import { TicketmasterMetadata } from '../../types';
import { MarketplaceSelector } from './SeatingChart.MarketplaceSelector';
import { MarketplaceLinks } from './SeatingChart.MarketplaceLinks';
import { EventMappingProvider } from '../EventMapping/EventMapping.hooks';
import { useFlag } from '@unleash/proxy-client-react';
import { getMarketplaceLogo } from '../../img/marketPlaceImage';

dayjs.extend(weekOfYear);
dayjs.extend(advancedFormats);

export function Header() {
  const {
    showEventMapping,
    setShowEventMapping,
    selectedMarketplaceEvent,
    isLoading,
    fetchUpdatedSeatingChart,
    isEditMarketplaceLinksDialogOpen,
    toggleForceSectionChecklist,
    setRule,
    boostConfirm,
    isBoosted,
    isBoostingAllowed,
    isSeatingChartError,
    selectedMarketplaceId,
    selectedEvent,
    isSeasonView,
    isMarketSelectorOpen,
    setIsMarketSelectorOpen,
    isMarketplaceLinksOpen,
    closeMarketplaceLinks,
    toggleMarketplaceLinks,
  } = useSeatingChart(
    'showEventMapping',
    'setShowEventMapping',
    'selectedMarketplaceEvent',
    'isLoading',
    'fetchUpdatedSeatingChart',
    'isEditMarketplaceLinksDialogOpen',
    'toggleForceSectionChecklist',
    'setRule',
    'boostConfirm',
    'isBoosted',
    'isBoostingAllowed',
    'isSeatingChartError',
    'selectedMarketplaceId',
    'selectedEvent',
    'isSeasonView',
    'isMarketSelectorOpen',
    'setIsMarketSelectorOpen',
    'isMarketplaceLinksOpen',
    'closeMarketplaceLinks',
    'toggleMarketplaceLinks',
  );

  const useNewMarketSelector = useFlag('new-marketplace-selector');
  const [supportOpen, { open: openSupport, close: closeSupport }] = useDisclosure();
  const marketplaceLogo = getMarketplaceLogo(selectedMarketplaceEvent.marketplaceId);
  const shouldFetchEventDetails = useMemo(() => {
    if (selectedMarketplaceEvent?.marketplaceEventId === undefined) {
      return false;
    }

    return (
      selectedMarketplaceEvent.marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo ||
      (selectedMarketplaceEvent.marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster && !selectedMarketplaceEvent.marketplaceEvent?.url)
    );
  }, [selectedMarketplaceEvent?.marketplaceEventId, selectedMarketplaceEvent.marketplaceId, selectedMarketplaceEvent.marketplaceEvent?.url]);

  const { data: fetchedEventUrl } = useGetApiMarketplacesMarketplaceIdEventsEventId(selectedMarketplaceEvent!.marketplaceId, selectedMarketplaceEvent.marketplaceEventId!, {
    query: {
      enabled: shouldFetchEventDetails,
      select(data) {
        return data.data.url;
      },
    },
    axios: {
      headers: {
        'x-tenant-id': selectedEvent?.tenantId,
      },
    },
  });

  const eventUrl = useMemo(() => {
    if (selectedMarketplaceEvent?.marketplaceEventId === undefined) {
      return '';
    }

    switch (selectedMarketplaceEvent.marketplaceId) {
      case BrokerNerdsBarkerCoreEnumsMarketplace.SeatGeek:
        return generateSeatGeekUrl(selectedMarketplaceEvent.marketplaceEventId);
      case BrokerNerdsBarkerCoreEnumsMarketplace.TicketEvolution:
        return generateTicketEvolutionUrl(selectedMarketplaceEvent.marketplaceEventId);
      case BrokerNerdsBarkerCoreEnumsMarketplace.TicketNetwork:
        return generateTicketNetworkUrl(selectedMarketplaceEvent.marketplaceEventId);
      case BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo:
      case BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster:
        return selectedMarketplaceEvent.marketplaceEvent?.url ?? fetchedEventUrl ?? '';
      default:
        return '';
    }
  }, [selectedMarketplaceEvent.marketplaceEventId, selectedMarketplaceEvent.marketplaceId, selectedMarketplaceEvent.marketplaceEvent?.url, fetchedEventUrl]);

  const isTicketmasterResaleEnabled = useMemo(() => {
    if (selectedMarketplaceEvent.marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster && selectedMarketplaceEvent.marketplaceEvent?.metadata) {
      try {
        const metadata = JSON.parse(selectedMarketplaceEvent.marketplaceEvent.metadata) as TicketmasterMetadata;
        return metadata.IsResaleEnabled;
      } catch {
        return false;
      }
    }
    return false;
  }, [selectedMarketplaceEvent]);

  const { tenants } = useGlobalState('tenants');
  const tenantPointOfSale = useMemo(() => tenants?.find((t) => t.tenantId === selectedEvent?.tenantId)?.pointOfSaleId || 'Unknown', [selectedEvent?.tenantId, tenants]);

  return (
    <>
      <Group pr={16} pl={14} py={10} gap="xs" align="center" justify="space-between" wrap="nowrap" className={classes.seatingChartHeaderWrapper}>
        <Group p={0} pl={2} gap="xs" h={44} wrap="nowrap" className={classes.seatingChartHeaderLeft}>
          <Popover
            position="bottom"
            withArrow
            opened={isMarketSelectorOpen && !isEditMarketplaceLinksDialogOpen}
            closeOnClickOutside
            onClose={() => {
              setIsMarketSelectorOpen(false);
            }}
          >
            <Box className={classes.indicatorBox}>
              <Popover.Target>
                {typeof marketplaceLogo !== 'string' ? (
                  <Tooltip label="Change Marketplace" position="right" withArrow>
                    <Avatar
                      data-market-selector-toggle
                      key="seatgeek"
                      m={0}
                      radius="xl"
                      variant="outline"
                      className={classes.avatar}
                      color="var(--colors-darkPaper)"
                      onClick={() => {
                        closeMarketplaceLinks();
                        setIsMarketSelectorOpen(!isMarketSelectorOpen);
                      }}
                    >
                      <ActionIcon>
                        <SyncAltIcon color="var(--colors-gray-5)" size={20} />
                      </ActionIcon>
                      {marketplaceLogo}
                    </Avatar>
                  </Tooltip>
                ) : (
                  <Tooltip label="Change Marketplace" position="right" withArrow>
                    <Avatar
                      key="other"
                      m={0}
                      radius="xl"
                      variant="outline"
                      className={classes.avatar}
                      color="var(--colors-darkPaper)"
                      onClick={() => {
                        closeMarketplaceLinks();
                        setIsMarketSelectorOpen(!isMarketSelectorOpen);
                      }}
                    >
                      <ActionIcon>
                        <SyncAltIcon color="var(--colors-gray-5)" size={20} />
                      </ActionIcon>
                      <img className="mantine-Avatar-image" height={36} width={36} src={marketplaceLogo} alt="Selected Marketplace" />
                    </Avatar>
                  </Tooltip>
                )}
              </Popover.Target>

              {isBoosted && useNewMarketSelector && (
                <Tooltip label="Boosted! This event will auto-price at the fastest interval" position="right" withArrow>
                  <Center h={16} w={16} bg="var(--colors-divider)" className={classes.indicatorWrapper}>
                    <RocketLaunchIcon color="var(--colors-iconFill)" size={20} />
                  </Center>
                </Tooltip>
              )}
            </Box>

            {!useNewMarketSelector && (
              <Popover.Dropdown>
                <MarketplaceSelector
                  onChange={() => {
                    setRule((rule) => ({ ...rule, filters: { ...rule.filters, sectionIds: [] } }));
                  }}
                  onClose={() => setIsMarketSelectorOpen(false)}
                />
              </Popover.Dropdown>
            )}
          </Popover>
          <Box className={classes.listingText}>
            {isLoading ? (
              <>
                <Skeleton height={8} maw={306} width="100%" my={7} />
                <Skeleton height={8} maw={200} width="100%" my={7} />
              </>
            ) : selectedMarketplaceEvent?.marketplaceEvent ? (
              <>
                {isSeasonView && (
                  <>
                    <Text size="sm" fw={600} truncate w="100%">
                      {selectedEvent?.enrichmentData?.seasonDetails?.name}
                    </Text>
                    <Text p={0} c="var(--colors-gray-5)" size="xs" truncate>
                      <Tooltip
                        label={`${selectedMarketplaceEvent?.marketplaceEvent.venueName} · ${selectedMarketplaceEvent?.marketplaceEvent.city}, ${selectedMarketplaceEvent?.marketplaceEvent.region}`}
                      >
                        <span>
                          {selectedMarketplaceEvent?.marketplaceEvent.venueName} · {selectedMarketplaceEvent?.marketplaceEvent.city},{' '}
                          {selectedMarketplaceEvent?.marketplaceEvent.region}
                        </span>
                      </Tooltip>
                    </Text>
                  </>
                )}
                {!isSeasonView && (
                  <>
                    <Text size="sm" fw={600} truncate w="100%">
                      {selectedMarketplaceEvent?.marketplaceEvent?.name}
                    </Text>
                    <Text p={0} c="var(--colors-gray-5)" size="xs" truncate>
                      <Tooltip
                        label={`${selectedMarketplaceEvent?.marketplaceEvent.venueName} · ${selectedMarketplaceEvent?.marketplaceEvent.city}, ${selectedMarketplaceEvent?.marketplaceEvent.region}`}
                      >
                        <span>
                          <span className={classes.desc}>{formatDate(selectedMarketplaceEvent?.marketplaceEvent.dateTimeLocal, DateFormats.Extended)}</span> -{' '}
                          {selectedMarketplaceEvent?.marketplaceEvent.venueName} · {selectedMarketplaceEvent?.marketplaceEvent.city},{' '}
                          {selectedMarketplaceEvent?.marketplaceEvent.region}
                        </span>
                      </Tooltip>
                    </Text>
                  </>
                )}
              </>
            ) : (
              <>
                <Text size="sm" fw={600} className={classes.name}>
                  Marketplace data not found
                </Text>
                {selectedEvent && (
                  <Text p={0} c="var(--colors-gray-5)" size="xs" fw={500} className={classes.descWrap}>
                    <span>
                      <span>
                        Check back later or{' '}
                        <UnstyledButton
                          fz="xs"
                          td="underline"
                          onClick={() => {
                            setShowEventMapping(!showEventMapping);
                            closeMarketplaceLinks();
                            setIsMarketSelectorOpen(false);
                          }}
                          className={classes.grayLink}
                        >
                          update the event mapping
                        </UnstyledButton>
                      </span>
                    </span>
                  </Text>
                )}
                {!selectedEvent && (
                  <Text p={0} c="var(--colors-gray-5)" size="xs" fw={500} className={classes.descWrap}>
                    Please update your search query
                  </Text>
                )}
              </>
            )}
          </Box>
          {/* {selectedMarketplaceEvent?.isUserMapped && (
              <Group p={0} align="center">
                <Tooltip label="Custom-mapped event" withArrow>
                  <Box
                    className={classes.indicatorWrapper}
                    onClick={() => {
                      setShowEventMapping(true);
                    }}
                  >
                    <LinkIcon color="var(--colors-gray-5)" />
                  </Box>
                </Tooltip>
              </Group>
            )} */}
        </Group>
        <Group wrap="nowrap" gap={4} style={{ flexShrink: 0 }}>
          {isBoosted && !useNewMarketSelector && (
            <Tooltip label="Boosted! This event will auto-price at the fastest interval" withArrow>
              <Center h={28} w={28} bg="var(--colors-divider)" className={classes.indicatorWrapper}>
                <RocketLaunchIcon color="var(--colors-iconFill)" size={18} />
              </Center>
            </Tooltip>
          )}
          {isTicketmasterResaleEnabled && (
            <Tooltip label="Ticketmaster Resale Enabled" withArrow>
              <Center h={28} w={28} bg="var(--colors-divider)" className={classes.indicatorWrapper}>
                <CachedIcon color="#d0006f" size={20} />
              </Center>
            </Tooltip>
          )}

          {useNewMarketSelector && (
            <Popover
              position="bottom"
              width={600}
              withinPortal
              withArrow
              opened={isMarketplaceLinksOpen}
              closeOnClickOutside
              onChange={toggleMarketplaceLinks}
              onClose={closeMarketplaceLinks}
            >
              <Popover.Target>
                <Tooltip label="Marketplace Links" withArrow position="left">
                  <ActionIcon
                    data-market-links-toggle
                    className={isMarketplaceLinksOpen ? classes.linksButtonActive : classes.linksButton}
                    onClick={() => {
                      toggleMarketplaceLinks();
                      setIsMarketSelectorOpen(false);
                    }}
                  >
                    <span style={{ transform: 'rotate(-45deg)' }}>
                      <LinkIcon size={20} />
                    </span>
                  </ActionIcon>
                </Tooltip>
              </Popover.Target>
            </Popover>
          )}

          <Menu withArrow width={200}>
            <Menu.Target>
              <ActionIcon>
                <MoreVertIcon size={24} color="var(--colors-gray-5)" />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {selectedMarketplaceEvent?.marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.TicketEvolution &&
                (selectedMarketplaceEvent?.marketplaceEventId ? (
                  <Menu.Item component="a" target="_blank" href={eventUrl} leftSection={<OpenNewIcon />}>
                    View in Core
                  </Menu.Item>
                ) : (
                  <Menu.Item leftSection={<OpenNewIcon />} disabled>
                    View in Core
                  </Menu.Item>
                ))}
              {selectedMarketplaceEvent?.marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.SeatGeek &&
                (selectedMarketplaceEvent?.marketplaceEventId ? (
                  <Menu.Item component="a" target="_blank" href={eventUrl} leftSection={<OpenNewIcon />}>
                    View on SeatGeek
                  </Menu.Item>
                ) : (
                  <Menu.Item leftSection={<OpenNewIcon />} disabled>
                    View on SeatGeek
                  </Menu.Item>
                ))}
              {selectedMarketplaceEvent?.marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.TicketNetwork &&
                (selectedMarketplaceEvent?.marketplaceEventId ? (
                  <Menu.Item component="a" target="_blank" href={eventUrl} leftSection={<OpenNewIcon />}>
                    View on TicketNetwork
                  </Menu.Item>
                ) : (
                  <Menu.Item leftSection={<OpenNewIcon />} disabled>
                    View on TicketNetwork
                  </Menu.Item>
                ))}
              {selectedMarketplaceEvent?.marketplaceId &&
                [BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster, BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo].includes(selectedMarketplaceEvent.marketplaceId) && (
                  <Menu.Item
                    component={selectedMarketplaceEvent.marketplaceEvent?.url ? 'a' : undefined}
                    target={selectedMarketplaceEvent.marketplaceEvent?.url ? '_blank' : undefined}
                    href={selectedMarketplaceEvent.marketplaceEvent?.url ?? undefined}
                    leftSection={<OpenNewIcon />}
                    disabled={!selectedMarketplaceEvent.marketplaceEvent?.url}
                  >
                    View on {selectedMarketplaceEvent.marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo ? 'Ticombo' : 'Ticketmaster'}
                  </Menu.Item>
                )}
              <Menu.Item
                onClick={() => {
                  setShowEventMapping(!showEventMapping);
                  setIsMarketSelectorOpen(false);
                  closeMarketplaceLinks();
                }}
                leftSection={<AppRegistrationIcon />}
              >
                Edit Event Mapping
              </Menu.Item>
              <Menu.Item
                disabled={!selectedMarketplaceEvent.marketplaceEventId || isSeatingChartError || !selectedMarketplaceEvent.svgDetails?.chartSvgUrl}
                onClick={() => {
                  setShowEventMapping(false);
                  toggleForceSectionChecklist();
                  setIsMarketSelectorOpen(false);
                  closeMarketplaceLinks();
                }}
                leftSection={<CheckBoxIcon />}
                data-pendo-name="Section Checklist"
              >
                Section Checklist
              </Menu.Item>
              {isBoostingAllowed && (
                <Menu.Item disabled={isBoosted} onClick={() => boostConfirm()} leftSection={<RocketLaunchIcon />} data-pendo-name="Boost Event">
                  Boost Event
                </Menu.Item>
              )}
              <Menu.Item leftSection={<SyncIcon />} onClick={() => fetchUpdatedSeatingChart()}>
                Check for Updates
              </Menu.Item>
              <Menu.Item
                leftSection={<LiveHelpIcon />}
                onClick={() => {
                  openSupport();
                  setIsMarketSelectorOpen(false);
                  closeMarketplaceLinks();
                }}
                style={{ borderBottom: 'none' }}
              >
                Request Support
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
        {supportOpen && (selectedMarketplaceEvent?.marketplaceEvent || (!selectedMarketplaceEvent.marketplaceEvent && selectedEvent)) && (
          <Modal
            opened={supportOpen}
            onClose={closeSupport}
            closeOnClickOutside={false}
            title={
              <Group gap="xs">
                <LiveHelpIcon color="var(--colors-iconFill)" />
                <Text size="sm">Support Request - Marketplace</Text>
              </Group>
            }
            centered
            size="md"
            className={classes.supportModal}
            // temp fix to show large select box options
          >
            {selectedMarketplaceEvent?.marketplaceEvent && (
              <SupportForm
                type="marketplaceEvent"
                marketplaceId={selectedMarketplaceEvent.marketplaceId}
                event={selectedMarketplaceEvent.marketplaceEvent}
                onClose={closeSupport}
              />
            )}
            {!selectedMarketplaceEvent.marketplaceEvent && selectedEvent && (
              <SupportForm type="marketplaceMissingEvent" marketplaceId={selectedMarketplaceId} event={selectedEvent} pointOfSaleId={tenantPointOfSale} onClose={closeSupport} />
            )}
          </Modal>
        )}
      </Group>
      {isMarketSelectorOpen && useNewMarketSelector && (
        <MarketplaceSelector
          onChange={() => {
            setRule((rule) => ({ ...rule, filters: { ...rule.filters, sectionIds: [] } }));
          }}
          onClose={() => setIsMarketSelectorOpen(false)}
        />
      )}
      {isMarketplaceLinksOpen && useNewMarketSelector && (
        <EventMappingProvider>
          <MarketplaceLinks />
        </EventMappingProvider>
      )}
    </>
  );
}
